import React, { useState } from 'react'
import { Col, Modal as ModalBS, Row } from 'react-bootstrap'
import type { PivotLayout } from '@ztechapps/common.contract'
import type { PivotData } from '../pivot/types'
import { BusyButton, SaveCancelButtons } from '@ztechapps/webui.button'
import { Modal, ModalDragHandle } from '@ztechapps/webui.modal'
import { HorizontalStack } from '@ztechapps/webui.util'
import { isPivotCalculatedMeasure } from '@ztechapps/common.utils'
import { PivotZoneEnum } from '../pivot/PivotZoneEnum'
import { usePivotViewerContext } from '../PivotViewer/Context'
import { FieldList } from './FieldList'
import { FieldZone } from './FieldZone'
import { PivotCalculatedValueModal } from './PivotCalculatedValueModal'

/** Props for the pivot fields dialog. */
export type PivotFieldsModalProps = {
  data: PivotData
  layout: PivotLayout
  timeZone: string
  onApply: (layout: PivotLayout) => void
}

type PivotFieldsBodyProps = PivotFieldsModalProps & { onClose: () => void }

// the dialog unmounts its body when hidden, so the draft below starts from the applied layout on every open
// staged Apply follows PivotOptionsModal's
const PivotFieldsBody = ({ data, layout, timeZone, onApply, onClose }: PivotFieldsBodyProps) => {
  const [draft, setDraft] = useState(layout)
  const takenNames = [
    ...data.fields.map((field) => field.name),
    ...draft.measures.filter(isPivotCalculatedMeasure).map((measure) => measure.field),
  ]

  return (
    <>
      <ModalDragHandle>Fields</ModalDragHandle>
      <ModalBS.Body>
        <div className="small text-secondary">Drag and drop fields to arrange</div>
        <Row className="g-3">
          <Col md={4}>
            <FieldList data={data} layout={draft} onChange={setDraft} />
          </Col>
          <Col md={8}>
            <HorizontalStack className="justify-content-end">
              <PivotCalculatedValueModal
                trigger={({ onClick }) => (
                  <BusyButton
                    variant="outline-secondary"
                    bsSize="sm"
                    icon="calculator"
                    text="Add calculated value"
                    onClick={onClick}
                  />
                )}
                data={data}
                takenNames={takenNames}
                onApply={(measure) => setDraft({ ...draft, measures: [...draft.measures, measure] })}
              />
            </HorizontalStack>
            <Row className="g-3">
              {[PivotZoneEnum.Filters, PivotZoneEnum.Columns, PivotZoneEnum.Rows, PivotZoneEnum.Measures].map(
                (zone) => (
                  <Col key={zone} md={6}>
                    <FieldZone zone={zone} layout={draft} timeZone={timeZone} data={data} onChange={setDraft} />
                  </Col>
                )
              )}
            </Row>
          </Col>
        </Row>
      </ModalBS.Body>
      <ModalBS.Footer>
        <SaveCancelButtons
          saveText="Apply"
          onSave={() => {
            onApply(draft)
            onClose()
          }}
          onCancel={onClose}
        />
      </ModalBS.Footer>
    </>
  )
}

/** Toolbar dialog staging the pivot field arrangement until applied. */
export const PivotFieldsModal = ({ data, layout, timeZone, onApply }: PivotFieldsModalProps) => {
  const { container } = usePivotViewerContext()

  return (
    <Modal
      trigger={({ onClick }) => <BusyButton icon="bars" text="Fields" onClick={onClick} />}
      size="xl"
      resizable
      resizeStorageKey="pivot-fields"
      container={container}
    >
      {({ toggleShowModal }) => (
        <PivotFieldsBody data={data} layout={layout} timeZone={timeZone} onApply={onApply} onClose={toggleShowModal} />
      )}
    </Modal>
  )
}
